import type { PreparationResult } from '../types';
import { ValueSellingSineWaveChart } from './ValueSellingSineWaveChart';

interface PreparationCardsProps {
  result: PreparationResult;
}

const normalizeListItem = (item: string): string =>
  item.replace(/\s*\n+\s*/g, ' ').replace(/\s+/g, ' ').trim();

const renderList = (items: string[], emptyFallback: string) => {
  const cleanItems = items.map(normalizeListItem).filter((item) => item.length > 0);

  if (!cleanItems.length) {
    return <p className="card__empty">{emptyFallback}</p>;
  }

  return (
    <ul className="card__list">
      {cleanItems.map((item, index) => (
        <li key={`${item.slice(0, 28)}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const radarItems: Array<{ key: keyof PreparationResult['conversationRadar']; label: string }> = [
  { key: 'pressaoPorPreco', label: 'Pressão por preço' },
  { key: 'riscoConcorrencial', label: 'Risco concorrencial' },
  { key: 'potencialDaConta', label: 'Potencial da conta' },
  { key: 'nivelDeResistencia', label: 'Nível de resistência' },
  { key: 'aberturaAoPremium', label: 'Abertura ao premium' }
];

const translatePriority = (priority: PreparationResult['metadata']['prioridade']): string => {
  const map: Record<PreparationResult['metadata']['prioridade'], string> = {
    margem: 'Prioridade: Margem',
    expansao: 'Prioridade: Expansão',
    retencao: 'Prioridade: Retenção',
    competicao: 'Prioridade: Competição',
    diagnostico: 'Prioridade: Diagnóstico'
  };

  return map[priority];
};

const translateTheme = (theme: PreparationResult['metadata']['primaryTheme']): string => {
  const map: Record<PreparationResult['metadata']['primaryTheme'], string> = {
    adubo: 'Tema-chave: Adubação/Fertilizantes',
    defensivo: 'Tema-chave: Defensivos',
    semente: 'Tema-chave: Sementes',
    biologico: 'Tema-chave: Biológicos',
    irrigacao: 'Tema-chave: Irrigação',
    maquinario: 'Tema-chave: Máquinas/Implementos',
    servicos: 'Tema-chave: Serviços técnicos',
    geral: 'Tema-chave: Estratégia comercial geral'
  };

  return map[theme];
};

const translateBehaviorProfile = (profile: PreparationResult['producerProfile']): string => {
  const map: Record<PreparationResult['producerProfile'], string> = {
    analitico: 'Perfil: Analítico',
    diretivo: 'Perfil: Diretivo',
    integrador: 'Perfil: Integrador',
    expressivo: 'Perfil: Expressivo'
  };

  return map[profile];
};

const translateClientPrioritySegment = (segment: PreparationResult['clientPrioritySegment']): string => {
  const map: Record<PreparationResult['clientPrioritySegment'], string> = {
    avaliar: 'Classificação: Avaliar',
    conquistar: 'Classificação: Conquistar',
    incentivar: 'Classificação: Incentivar',
    blindar: 'Classificação: Blindar'
  };

  return map[segment];
};

const translateSelectedCulture = (culture: PreparationResult['selectedCulture']): string => {
  const map: Record<PreparationResult['selectedCulture'], string> = {
    soja: 'Cultura: Soja',
    milho: 'Cultura: Milho',
    trigo: 'Cultura: Trigo',
    cevada: 'Cultura: Cevada',
    cafe: 'Cultura: Café',
    citros: 'Cultura: Citros',
    uva: 'Cultura: Uva',
    banana: 'Cultura: Banana',
    hortifruti: 'Cultura: Hortifruti',
    tomate: 'Cultura: Tomate',
    batata: 'Cultura: Batata',
    cebola: 'Cultura: Cebola',
    cana: 'Cultura: Cana-de-açúcar',
    algodao: 'Cultura: Algodão',
    feijao: 'Cultura: Feijão',
    arroz: 'Cultura: Arroz',
    sorgo: 'Cultura: Sorgo',
    pastagem: 'Cultura: Pastagem',
    frutas: 'Cultura: Frutas',
    vegetais: 'Cultura: Vegetais',
    pecuaria: 'Cultura: Pecuária',
    outra: 'Cultura: Outra',
    geral: 'Cultura: Geral'
  };

  return map[culture];
};

export const PreparationCards = ({ result }: PreparationCardsProps) => {
  const generatedAt = new Date(result.metadata.generatedAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <section className="results" aria-live="polite">
      <div className="results__heading animate-in">
        <h2>{result.tituloFicha}</h2>
        <p>Cenário analisado: {result.cenarioOriginal}</p>
        <div className="results__meta">
          <span>{generatedAt}</span>
          <span>{translateBehaviorProfile(result.producerProfile)}</span>
          <span>{translateClientPrioritySegment(result.clientPrioritySegment)}</span>
          <span>{translateSelectedCulture(result.selectedCulture)}</span>
          <span>{translatePriority(result.metadata.prioridade)}</span>
          <span>{translateTheme(result.metadata.primaryTheme)}</span>
          {result.metadata.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="results__tag">
              {tag.replace('_', ' ')}
            </span>
          ))}
        </div>
      </div>

      <section className="results__executive animate-in">
        <article className="card card--executive-radar">
          <div className="card__title-row">
            <h3>Radar da Conversa</h3>
            <p className="card__kicker">Leitura visual do nível de pressão e oportunidade</p>
          </div>
          <div className="radar">
            {radarItems.map(({ key, label }) => {
              const value = result.conversationRadar[key];
              return (
                <div key={key} className="radar__row">
                  <div className="radar__label-row">
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                  <div className="radar__track" aria-hidden="true">
                    <div className="radar__fill" style={{ width: `${value}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>Orientação Comportamental</h3>
          <p className="card__kicker">Como conduzir a conversa com esse perfil</p>
        </div>
        <div className="card__grid card__grid--thirds">
          <div className="card__section">
            <h4>Linguagem Recomendada</h4>
            {renderList(result.behavioralGuidance.linguagemRecomendada, 'Sem orientação de linguagem.')}
          </div>
          <div className="card__section">
            <h4>Postura Recomendada</h4>
            {renderList(result.behavioralGuidance.posturaRecomendada, 'Sem orientação de postura.')}
          </div>
          <div className="card__section">
            <h4>Ritmo da Conversa</h4>
            {renderList(result.behavioralGuidance.ritmoDaConversa, 'Sem orientação de ritmo.')}
          </div>
        </div>
        <div className="card__section">
          <h4>Forma de Fechamento</h4>
          {renderList(result.behavioralGuidance.formaDeFechamento, 'Sem orientação de fechamento.')}
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>OPC</h3>
          <p className="card__kicker">Estratégia da Conversa</p>
        </div>
        <div className="card__section">
          <h4>Objetivo Central da Conversa</h4>
          {renderList(result.opc.objetivo, 'Sem objetivos sugeridos para este cenário.')}
        </div>
        <div className="card__section">
          <h4>Processo de Condução</h4>
          {renderList(result.opc.processo, 'Sem recomendações de processo para este cenário.')}
        </div>
        <div className="card__section">
          <h4>Compromisso ao Final</h4>
          {renderList(result.opc.compromisso, 'Sem compromissos sugeridos para este cenário.')}
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>SPIN Selling</h3>
          <p className="card__kicker">Perguntas que movem a decisão (Entender para Atender)</p>
        </div>
        <div className="card__grid">
          <div className="card__section">
            <h4>Situação</h4>
            {renderList(result.spin.situacao, 'Sem perguntas de situação.')}
          </div>
          <div className="card__section">
            <h4>Problema</h4>
            {renderList(result.spin.problema, 'Sem perguntas de problema.')}
          </div>
          <div className="card__section">
            <h4>Implicação</h4>
            {renderList(result.spin.implicacao, 'Sem perguntas de implicação.')}
          </div>
          <div className="card__section">
            <h4>Necessidade de Solução</h4>
            {renderList(result.spin.necessidadeSolucao, 'Sem perguntas de necessidade de solução.')}
          </div>
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>EPA / Challenger</h3>
          <p className="card__kicker">Provocações de Valor</p>
        </div>
        <div className="card__grid card__grid--thirds">
          <div className="card__section">
            <h4>Educar</h4>
            {renderList(result.epa.educar, 'Sem provocações de educação para este cenário.')}
          </div>
          <div className="card__section">
            <h4>Personalizar</h4>
            {renderList(result.epa.personalizar, 'Sem orientações de personalização para este cenário.')}
          </div>
          <div className="card__section">
            <h4>Assumir o Controle</h4>
            {renderList(result.epa.assumirControle, 'Sem orientações de controle para este cenário.')}
          </div>
        </div>
      </article>

      <ValueSellingSineWaveChart map={result.valueSineMap} />
    </section>
  );
};
